/**
 * 참가 API 클라이언트 (TASK-2026-004 SPEC 「포함 범위」 1·5, 중앙 명세 F1-03·OF1-02).
 *
 * TASK-2026-003이 만든 계약(`sources/server/openapi.json`)을 그대로 쓴다. 이 모듈이
 * 지키는 성질은 세 가지다.
 *
 * 1. **주소를 받지 않는다.** 경로는 이 파일의 상수뿐이고 base URL 인자가 없다. 그래서
 *    설정이 틀리든 질의값이 조작되든 다른 origin으로 자료가 나갈 방법이 없다. `fetch`에
 *    `mode: 'same-origin'`을 붙여 브라우저 수준에서도 교차 origin 요청을 막는다.
 * 2. **동의한 세션만 쓴다.** 세션 cookie는 HttpOnly라 코드가 읽지 못하고, 쓰기 요청은
 *    서버가 준 CSRF 토큰이 있어야 만들어진다. 토큰이 없으면 요청을 **보내지 않고** 던진다.
 *    동의 전·연습 모드에서 원격 계측이 0이어야 하므로(OF1-02) 여기서 한 번 더 막는다.
 * 3. **실패를 성공으로 바꾸지 않는다.** 모든 실패는 `ApiError`이고, 다시 시도해도 되는
 *    실패인지(`retriable`)를 서버 코드로 구별한다. 본문을 못 읽어도 상태 코드는 남긴다.
 */

const API = '/api/v1';
const PATHS = Object.freeze({
  publicConfig: `${API}/public-config`,
  sessionIntents: `${API}/session-intents`,
  recoverIntent: `${API}/session-intents/recover`,
  sessions: `${API}/sessions`,
  session: `${API}/session`,
  batches: `${API}/session/batches`,
  response: `${API}/session/response`,
  complete: `${API}/session/complete`,
  result: `${API}/session/result`,
  withdraw: `${API}/session/withdraw`,
});

export const CSRF_HEADER = 'x-ccatlas-csrf';

/** 본문을 읽지 못한 성공 응답에 붙이는 상태. 통신 실패(0)와 구별한다. */
const UNREADABLE_STATUS = 200;

/** 요청 하나의 제한 시간. 넘기면 중단하고 재시도 대상으로 남긴다. */
export const REQUEST_TIMEOUT_MS = 15_000;

export class ApiError extends Error {
  /** 서버가 준 제한된 오류 코드. 통신 자체가 실패하면 `network_error`. */
  readonly code: string;
  /** HTTP 상태. 통신 실패는 0이다. */
  readonly status: number;
  /** 다시 시도해도 되는 실패인가. 대기열이 이 값으로 정리 여부를 정한다. */
  readonly retriable: boolean;
  /** 서버가 실은 부가 정보(코드 외 필드). 원문은 담기지 않는다. */
  readonly detail: Readonly<Record<string, unknown>>;

  constructor(status: number, code: string, detail: Readonly<Record<string, unknown>> = {}) {
    super(`${String(status)} ${code}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
    /*
     * 다시 보내도 되는 실패: 통신 오류(0), 제한(429), 서버·저장소 오류(5xx).
     * 나머지(4xx)는 같은 요청을 다시 보내도 같은 답이 온다. 대기열이 무한히
     * 같은 요청을 돌리지 않게 여기서 한 번만 판정한다.
     */
    this.retriable = status === 0 || status === 429 || status >= 500;
  }
}

export interface PublicConfig {
  readonly campaign_id: string;
  readonly name: string;
  readonly mode: string;
  readonly protocol: string;
  readonly test_version: string;
  readonly consent_version: string;
  readonly consent_text: string;
  readonly open: boolean;
  /**
   * 진입 방식 (TASK-2026-011). `open`이면 초대코드 없이 시작한다. 이 필드를 모르는 서버의 응답에는
   * 없으므로 없으면 초대코드 캠페인으로 본다(닫힌 쪽).
   */
  readonly entry_mode?: 'open' | 'invite_code';
}

export interface SessionCreated {
  readonly session_ref: string;
  readonly csrf_token: string;
  readonly resume_until: number;
  /** 세션의 캠페인. 다른 링크에서 남긴 intent를 회수했는지 대조한다(TASK-2026-011). 이전 서버에는 없다. */
  readonly campaign_id?: string;
  /** 같은 intent로 이미 만들어진 세션을 돌려받았는가(CR-01 §5). */
  readonly recovered?: boolean;
}

/** intent 회수 결과. 아직 commit이 없으면 `pending`이다(CR-01 §4). */
export type IntentRecovery = { readonly pending: true } | ({ readonly pending: false } & SessionCreated);

export interface SessionState {
  readonly session_ref: string;
  readonly campaign_id: string;
  /** TASK-2026-011. 이 세션이 속한 코호트. 이전 서버 응답에는 없다. */
  readonly cohort_id?: string;
  readonly status: 'active' | 'withdrawn' | 'completed';
  readonly resume_until: number;
  readonly through_seq: number;
  readonly received_ranges: readonly { readonly first_seq: number; readonly last_seq: number }[];
  readonly event_count: number;
  readonly event_limit_reached: boolean;
  readonly response_version: number;
  readonly completed: boolean;
  readonly result_digest: string | null;
  readonly csrf_token: string;
}

export interface BatchAck {
  readonly batch_id: string;
  readonly duplicate: boolean;
  readonly through_seq: number;
}

export interface ResponseAck {
  readonly version: number;
  readonly final: boolean;
  readonly duplicate: boolean;
}

export interface CompleteOutcome {
  readonly complete: boolean;
  /** 결과 envelope. 완료되지 않아도 무엇이 막고 있는지 이 안에 있다. */
  readonly result: {
    readonly body: { readonly complete: boolean; readonly blocking_issues: readonly { readonly code: string }[] };
    readonly digest: string;
  };
}

export type AnswerValue = string | number | boolean | null | readonly string[];

export interface ResponseRequest {
  readonly base_version: number;
  readonly final: boolean;
  readonly answers: Readonly<Record<string, AnswerValue>>;
}

export interface CompleteRequest {
  readonly ending: 'all_cleared' | 'early_exit';
  readonly last_seq: number;
  readonly response_version: number;
}

export type Fetcher = (input: string, init?: RequestInit) => Promise<Response>;

export interface ApiClientOptions {
  readonly fetch: Fetcher;
  /** 현재 세션의 CSRF 토큰. 없으면 쓰기 요청을 만들지 않는다. */
  readonly csrf: () => string | undefined;
  /** 제한 시간을 거는 신호 생성기. 없으면 `AbortSignal.timeout`을 쓴다. */
  readonly timeoutSignal?: (ms: number) => AbortSignal | undefined;
}

interface RequestOptions {
  readonly method: 'GET' | 'POST' | 'PUT';
  readonly path: string;
  /** 이미 문자열로 만든 본문. batch는 canonical 문자열을 그대로 보낸다. */
  readonly bodyText?: string;
  /** 쓰기 요청인가. CSRF 토큰을 요구한다. */
  readonly write?: boolean;
}

function detailOf(value: unknown): { code: string; detail: Record<string, unknown> } {
  if (typeof value !== 'object' || value === null) {
    return { code: 'unreadable_error', detail: {} };
  }
  const record = value as Record<string, unknown>;
  const { code, ...rest } = record;
  return { code: typeof code === 'string' ? code : 'unreadable_error', detail: rest };
}

export interface ApiClient {
  publicConfig(campaignId: string): Promise<PublicConfig>;
  /**
   * 이 캠페인에 결속된 세션 생성 intent를 받는다 (TASK-2026-011 CR-01 §1, CR-02 §1). 토큰은 HttpOnly
   * cookie로만 오고 코드는 읽지 못한다. 동의 뒤에만 부른다.
   */
  issueSessionIntent(campaignId: string): Promise<{ readonly expires_at: number; readonly reused: boolean }>;
  /** `inviteCode`는 초대코드 캠페인에서만 싣는다. 공개 캠페인 요청에 실으면 서버가 400으로 거부한다. */
  createSession(input: {
    readonly campaignId: string;
    readonly inviteCode?: string;
    readonly consentVersion: string;
  }): Promise<SessionCreated>;
  /** 같은 intent로 commit된 결과를 회수한다. 새 세션을 만들지 않는다. */
  recoverSessionIntent(): Promise<IntentRecovery>;
  session(): Promise<SessionState>;
  sendBatch(canonicalText: string): Promise<BatchAck>;
  putResponse(request: ResponseRequest): Promise<ResponseAck>;
  complete(request: CompleteRequest): Promise<CompleteOutcome>;
  /**
   * 확정 결과를 **원문 그대로** 읽는다 (REV-007).
   *
   * 파싱해서 돌려주지 않는다. 참가자에게 보여 주고 내려받게 할 JSON은 서버가
   * 확정한 바로 그 바이트여야 한다. 다시 직렬화하면 digest가 가리키는 문자열과
   * 달라지고, 그 순간 화면의 사본은 확정본이 아니라 재조립본이 된다.
   */
  resultText(): Promise<string>;
  withdraw(): Promise<{ readonly status: string }>;
}

export function createApiClient(options: ApiClientOptions): ApiClient {
  const timeoutSignal =
    options.timeoutSignal ??
    ((ms: number) => {
      try {
        return AbortSignal.timeout(ms);
      } catch {
        return undefined;
      }
    });

  async function sendText(request: RequestOptions): Promise<string> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (request.bodyText !== undefined) {
      headers['content-type'] = 'application/json';
    }
    if (request.write === true) {
      const csrf = options.csrf();
      if (csrf === undefined || csrf === '') {
        /*
         * 세션이 없는데 쓰기를 시도했다. 요청을 만들지 않는다 — 서버가 403으로
         * 막아 주더라도, 동의 전에 원격으로 나가는 요청은 그 자체가 위반이다.
         */
        throw new ApiError(0, 'session_missing');
      }
      headers[CSRF_HEADER] = csrf;
    }

    let response: Response;
    try {
      const signal = timeoutSignal(REQUEST_TIMEOUT_MS);
      response = await options.fetch(request.path, {
        method: request.method,
        headers,
        credentials: 'same-origin',
        // 교차 origin 요청은 브라우저가 거부한다. 계약상 같은 origin만 쓴다.
        mode: 'same-origin',
        cache: 'no-store',
        redirect: 'error',
        ...(request.bodyText === undefined ? {} : { body: request.bodyText }),
        ...(signal === undefined ? {} : { signal }),
      });
    } catch {
      // 오프라인·중단·DNS 실패를 구별하지 않는다. 어느 쪽이든 다시 보내면 된다.
      throw new ApiError(0, 'network_error');
    }

    const text = await response.text().catch(() => '');
    if (!response.ok) {
      let parsed: unknown;
      try {
        parsed = text === '' ? undefined : JSON.parse(text);
      } catch {
        parsed = undefined;
      }
      const { code, detail } = detailOf(parsed);
      throw new ApiError(response.status, code, detail);
    }
    return text;
  }

  async function send<T>(request: RequestOptions): Promise<T> {
    const text = await sendText(request);
    try {
      return (text === '' ? undefined : JSON.parse(text)) as T;
    } catch {
      /*
       * 200인데 JSON이 아니다. 성공으로 바꾸지 않는다 —— 호출자가 `undefined`를
       * 받아 빈 값으로 진행하면 없는 응답을 있는 것처럼 다루게 된다.
       */
      throw new ApiError(UNREADABLE_STATUS, 'unreadable_response');
    }
  }

  return {
    async publicConfig(campaignId) {
      return send<PublicConfig>({
        method: 'GET',
        path: `${PATHS.publicConfig}?campaign=${encodeURIComponent(campaignId)}`,
      });
    },

    async issueSessionIntent(campaignId) {
      return send<{ expires_at: number; reused: boolean }>({
        method: 'POST',
        path: PATHS.sessionIntents,
        bodyText: JSON.stringify({ campaign_id: campaignId }),
      });
    },

    async createSession(input) {
      return send<SessionCreated>({
        method: 'POST',
        path: PATHS.sessions,
        // 동의는 참가자가 화면에서 한 것이고, 그 사실만 계약대로 싣는다.
        bodyText: JSON.stringify({
          campaign_id: input.campaignId,
          ...(input.inviteCode === undefined ? {} : { invite_code: input.inviteCode }),
          consent: true,
          consent_version: input.consentVersion,
        }),
      });
    },

    async recoverSessionIntent() {
      const body = await send<SessionCreated & { code?: string }>({ method: 'POST', path: PATHS.recoverIntent, bodyText: '{}' });
      // 202는 성공 상태지만 세션이 아니다. 본문의 코드로 구별한다.
      if (body.code === 'session_creation_pending') {
        return { pending: true };
      }
      return { pending: false, ...body };
    },

    async session() {
      return send<SessionState>({ method: 'GET', path: PATHS.session });
    },

    async sendBatch(canonicalText) {
      return send<BatchAck>({ method: 'POST', path: PATHS.batches, bodyText: canonicalText, write: true });
    },

    async putResponse(request) {
      return send<ResponseAck>({
        method: 'PUT',
        path: PATHS.response,
        bodyText: JSON.stringify({
          schema: 'ccatlas.response/1',
          base_version: request.base_version,
          final: request.final,
          answers: request.answers,
        }),
        write: true,
      });
    },

    async complete(request) {
      try {
        const outcome = await send<CompleteOutcome & { complete?: boolean }>({
          method: 'POST',
          path: PATHS.complete,
          bodyText: JSON.stringify({ schema: 'ccatlas.complete/1', ...request }),
          write: true,
        });
        return { complete: outcome.complete === true, result: outcome.result };
      } catch (error) {
        /*
         * 409 `incomplete`는 실패가 아니라 "아직 못 받은 자료가 있다"는 답이다.
         * 무엇이 막고 있는지가 본문에 있으므로 화면이 사실대로 보여 줄 수 있게
         * 결과를 돌려준다. 그 밖의 409(`already_completed`)는 그대로 던진다.
         */
        if (error instanceof ApiError && error.status === 409 && error.code === 'incomplete') {
          const result = error.detail['result'] as CompleteOutcome['result'] | undefined;
          if (result !== undefined) {
            return { complete: false, result };
          }
        }
        throw error;
      }
    },

    async resultText() {
      return sendText({ method: 'GET', path: PATHS.result });
    },

    async withdraw() {
      return send<{ status: string }>({ method: 'POST', path: PATHS.withdraw, bodyText: '{}', write: true });
    },
  };
}
